/**
 * Finish E2E setup for an alias whose create committed the identity and
 * session but never stored a signing key or key bundle.
 */

import {
  DEFAULT_MAX_REQUEST_BODY_BYTES,
  jsonUtf8ByteLength,
  type IdentityApi,
} from '@adieuu/shared';
import { clearBytes } from '@adieuu/crypto';
import { generateE2EKeys, getDefaultDeviceName } from './e2eKeyService';
import { storeDeviceKeys } from './deviceKeyStorage';
import { generateAndUploadPreKeys, type Platform } from './preKeyService';
import { buildDeviceStaticKeyAttestationB64 } from './deviceStaticKeyAttestationUpload';

/** True only when the alias has no signing key and the bundle was never stored. */
export function bundleMissingBecauseE2ENeverInitialized(
  hasE2EKeys: boolean | undefined,
  response: { success: boolean; error?: { code?: string } },
): boolean {
  return hasE2EKeys === false && !response.success && response.error?.code === 'NOT_FOUND';
}

function clearGeneratedSecrets(keys: {
  signingPrivateKey: Uint8Array;
  pqSigningPrivateKey?: Uint8Array;
  devicePrivateKeys: { ecdh: Uint8Array; kem: Uint8Array };
}): void {
  clearBytes(keys.signingPrivateKey);
  if (keys.pqSigningPrivateKey) clearBytes(keys.pqSigningPrivateKey);
  clearBytes(keys.devicePrivateKeys.ecdh);
  clearBytes(keys.devicePrivateKeys.kem);
}

export async function finishUninitializedE2E(input: {
  identityApi: IdentityApi;
  identityId: string;
  passphrase: string;
  wrappingKey: Uint8Array;
  platform: Platform;
}): Promise<
  | { ok: true; signingPrivateKey: Uint8Array; pqSigningPrivateKey: Uint8Array; deviceId: string }
  | { ok: false; error: string }
> {
  let e2eResult: Awaited<ReturnType<typeof generateE2EKeys>>;
  try {
    e2eResult = await generateE2EKeys({
      identityId: input.identityId,
      passphrase: input.passphrase,
      deviceName: getDefaultDeviceName(),
      cryptoProfile: 'default',
    });
  } catch (err) {
    console.error('[Identity] finishUninitializedE2E: key generation failed:', err);
    return { ok: false, error: 'Failed to generate encryption keys' };
  }

  clearBytes(e2eResult.webDevice.privateKeys.ecdh);
  clearBytes(e2eResult.webDevice.privateKeys.kem);

  if (!e2eResult.pqSigningPublicKey || !e2eResult.pqSigningPrivateKey) {
    clearGeneratedSecrets(e2eResult);
    return { ok: false, error: 'Failed to generate encryption keys' };
  }

  const pqSigningPrivateKey = e2eResult.pqSigningPrivateKey;

  try {
    const staticKeyAttestation = buildDeviceStaticKeyAttestationB64({
      signingPrivateKey: e2eResult.signingPrivateKey,
      profile: 'default',
      deviceId: e2eResult.device.deviceId,
      ecdhPublicKey: e2eResult.device.ecdhPublicKey,
      kemPublicKey: e2eResult.device.kemPublicKey,
      pqSigningPrivateKey,
    });
    const initBody = {
      signingPublicKey: e2eResult.signingPublicKey,
      pqSigningPublicKey: e2eResult.pqSigningPublicKey,
      preferredCryptoProfile: 'default' as const,
      device: { ...e2eResult.device, staticKeyAttestation },
      bundle: e2eResult.encryptedBundle,
    };
    const initBytes = jsonUtf8ByteLength(initBody);
    if (initBytes > DEFAULT_MAX_REQUEST_BODY_BYTES) {
      clearGeneratedSecrets(e2eResult);
      return {
        ok: false,
        error: `Encryption setup payload is too large (${(initBytes / 1024).toFixed(1)} KiB; max ${(DEFAULT_MAX_REQUEST_BODY_BYTES / 1024).toFixed(0)} KiB).`,
      };
    }

    const initResponse = await input.identityApi.initializeE2E(input.identityId, initBody);
    if (!initResponse.success) {
      console.error(
        '[Identity] finishUninitializedE2E: E2E initialize failed:',
        initResponse.error?.message,
      );
      clearGeneratedSecrets(e2eResult);
      return { ok: false, error: 'Failed to upload encryption keys to server' };
    }
  } catch (err) {
    console.error('[Identity] finishUninitializedE2E: E2E initialize threw:', err);
    clearGeneratedSecrets(e2eResult);
    return { ok: false, error: 'Failed to initialize encryption' };
  }

  try {
    await storeDeviceKeys(
      e2eResult.device.deviceId,
      input.identityId,
      e2eResult.devicePrivateKeys.ecdh,
      e2eResult.devicePrivateKeys.kem,
      input.wrappingKey,
      e2eResult.device.routingTag,
    );
  } catch (err) {
    console.error('[Identity] finishUninitializedE2E: failed to store device keys:', err);
    clearBytes(e2eResult.signingPrivateKey);
    clearBytes(pqSigningPrivateKey);
    return { ok: false, error: 'Failed to store device keys' };
  }

  try {
    await generateAndUploadPreKeys(
      {
        identityId: input.identityId,
        deviceId: e2eResult.device.deviceId,
        signingPrivateKey: e2eResult.signingPrivateKey,
        wrappingKey: input.wrappingKey,
        platform: input.platform,
        pqSigningPrivateKey,
      },
      input.identityApi,
    );
  } catch (err) {
    console.warn('[Identity] finishUninitializedE2E: pre-key upload failed (non-fatal):', err);
  }

  return {
    ok: true,
    signingPrivateKey: e2eResult.signingPrivateKey,
    pqSigningPrivateKey,
    deviceId: e2eResult.device.deviceId,
  };
}
